<template>
  <base-widget
    ref="widget"
    :title="$t('team.title')"
    :style="heightStyles"
    :should-apply-footer-background="false"
    scrollable
  >
    <!-- Hide for Phase 1 -->
    <template slot="right">
      <base-link-with-icon
        class="add-member"
        size="md"
        @click="onAdduser = true"
      >
        Add User
      </base-link-with-icon>
    </template>

    <template slot="after-heading">
      <div class="d-block">
        <div class="d-flex mb-2">
          <div
            class="search-bar"
            @click="trackSearch()"
          >
            <base-search
              :value="search"
              @input="onSearch"
            />
          </div>
        </div>
      </div>
    </template>
    <base-list
      :loading="loading"
      :items="items"
    >
      <template slot="loading">
        <base-skeleton
          v-for="n in 4"
          :key="n"
          :opacity="0 + (1 /n)"
        >
          <div class="d-flex align-items-center">
            <div class="skeleton__circle skeleton__circle--large mr-4" />

            <div class="w-100">
              <div
                class="skeleton__item"
                style="max-width: 150px"
              />
              <div
                class="skeleton__item"
                style="max-width: 100px"
              />
            </div>
          </div>
        </base-skeleton>
      </template>
      <template v-slot:nodata>
        <template v-if="search !== ''">
          <base-text
            slot="nodata"
            size="s"
            light
          >
            No results for "{{ search }}"
          </base-text>
        </template>
        <template v-else>
          <empty
            title="No team yet"
            subtitle="Add a team member to start your list"
          />
        </template>
      </template>
      <template v-slot:default="{item, index}">
        <team-cell
          :user="item.user"
          :last-active="item.last_active"
          :clients-assigned="item.clients_count"
          :based-in="item.location"
          :img="item.image"
          @click="$emit('click', index)"
        />
      </template>
    </base-list>
    <div
      class="base-pagination d-flex justify-content-center"
    >
      <base-pagination
        v-if="profilesCount"
        v-model="page"
        :count="profilesCount"
        :page-size="pageSize"
        :hide-if-no-pages="false"
        :should-apply-margin-top="false"
        @pageChange="pageChange"
      />
    </div>
    <!-- modal -->
    <base-modal
      id="newAlbumDialog"
      :show="onAdduser"
      :show-footer="true"
      size="modal-lg"
      title="Add User"
      class="add-modal"
      @closed="onAdduser = false"
    >
      <hr style="margin: 26.5px 0px 26.5px 0px">
      <div>
        <form
          novalidate
        >
          <validation-observer ref="addUserForm">
            <div class="add-user-grid">
              <div>
                <img
                  v-if="image"
                  :src="image"
                  alt
                  class="avatar"
                  @click="loadImage"
                >
                <div v-else class="position-relative">
                  <img
                    :src="require('@/assets/upload-avatar-placeholder.svg').default"
                    class="avatar-placeholder"
                    alt
                    @click="loadImage"
                  >
                  <img
                    :src="require('@/assets/upload-avatar-placeholder-add-icon.svg').default"
                    class="avatar-placeholder__add-icon"
                    alt
                    @click="loadImage"
                  >
                </div>
                <input
                  ref="upload"
                  type="file"
                  class="d-none"
                  accept="image/png, image/jpeg"
                  @change="readFile"
                >
              </div>
              <div class="add-client-grid__form">
                <!-- first name -->
                <div class="form-grid">
                  <validation-provider
                    ref="first_name_provider"
                    v-slot="{ touched, pristine, valid, invalid, validated }"
                    name="first_name"
                    :rules="{ required: isRequiredField('first_name') }"
                  >
                    <input
                      ref="first_name_provider"
                      v-model="first_name"
                      :style="[ error_firstname ? {'border':'1px solid red'}: { }]"
                      type="text"
                      name="first_name"
                      class="form-control"
                      tabindex="5"
                      :disabled="loading"
                      placeholder="First Name*"
                      autocomplete="off"
                      :class="{
                        'is-invalid':
                          validated && invalid && !onFocusFisrtName,
                      }"
                      @focus="onFocusFisrtName = true"
                      @blur="onFocusFisrtName = false"
                    >
                    <span
                      class="caption-regular text-error"
                    >
                      {{ validated && invalid && isRequiredField('first_name') && !onFocusFisrtName ? ' Required' : '' }}
                    </span>
                    <span
                      v-if="!invalid && !onFocusFisrtName"
                      class="caption-regular text-error"
                    >
                      {{ error_firstname }}
                    </span>
                  </validation-provider>
                  <!-- last name -->
                  <validation-provider
                    ref="last_name_provider"
                    v-slot="{ touched, pristine, valid, invalid, validated }"
                    name="last_name"
                    :rules="{ required: isRequiredField('last_name') }"
                  >
                    <input
                      v-model="last_name"
                      type="text"
                      :style="[ error_lastname ? {'border':'1px solid red'}: { }]"
                      name="last_name"
                      class="form-control"
                      tabindex="5"
                      :disabled="loading"
                      placeholder="Last Name*"
                      autocomplete="off"
                      :class="{
                        'is-invalid':
                          validated && invalid && !onFocusLastName,
                      }"
                      @focus="onFocusLastName = true"
                      @blur="onFocusLastName = false"
                    >
                    <span
                      class="caption-regular text-error"
                    >
                      {{ validated && invalid && isRequiredField('last_name') && !onFocusLastName ? ' Required' : '' }}
                    </span>
                    <span
                      v-if="!invalid && !onFocusLastName"
                      class="caption-regular text-error"
                    >
                      {{ error_lastname }}
                    </span>
                  </validation-provider>
                  <!-- email -->
                  <validation-provider
                    ref="email_provider"
                    v-slot="{ touched, pristine, valid, invalid, validated }"
                    name="email"
                    :rules="{
                      required: isRequiredField('email'),
                      email: isRequiredField('email'),
                    }"
                  >
                    <input
                      v-model="email"
                      type="email"
                      :style="[ error_email ? {'border':'1px solid red'}: { }]"
                      name="email"
                      class="form-control"
                      tabindex="3"
                      :disabled="loading"
                      placeholder="Email*"
                      autocomplete="off"
                      :class="{
                        'is-invalid':
                          validated && invalid && !onFocusEmail,
                      }"
                      @focus="onFocusEmail = true"
                      @blur="onFocusEmail = false"
                    >
                    <span
                      class="caption-regular text-error"
                    >
                      {{ validated && invalid && isRequiredField('email') && !onFocusEmail ? ' Required' : '' }}
                    </span>
                    <span
                      v-if="!invalid && !onFocusEmail"
                      class="caption-regular text-error"
                    >
                      {{ error_email }}
                    </span>
                  </validation-provider>
                  <!-- phone number -->
                  <validation-provider
                    ref="phone_number_provider"
                    v-slot="{ touched, pristine, valid, invalid, validated }"
                    name="phone_number"
                  >
                    <input
                      v-model="phone_number"
                      :style="[ error_phonenumber ? {'border':'1px solid red'}: { }]"
                      type="text"
                      name="phone_number"
                      class="form-control"
                      :class="{
                        'is-invalid':
                          validated && invalid,
                      }"
                      tabindex="4"
                      :disabled="loading"
                      placeholder="Phone Number"
                      autocomplete="off"
                      @focus="onFocusPhoneNumber = true"
                      @blur="onFocusPhoneNumber = false"
                    >
                    <span
                      class="caption-regular text-error"
                    >
                      {{ validated && invalid && isRequiredField('phone_number') ? ' Required' : '' }}
                    </span>
                    <span
                      v-if="!invalid && !onFocusPhoneNumber"
                      class="caption-regular text-error"
                    >
                      {{ error_phonenumber }}
                    </span>
                  </validation-provider>
                  <v-select
                    v-model="role"
                    label="label"
                    :clearable="false"
                    :searchable="false"
                    :options="role_list"
                    :reduce="x => x.value"
                    class="select-role"
                  >
                    <!-- <template v-slot:append>
                      <v-icon>caret-down</v-icon>
                    </template> -->
                  </v-select>

                  <div
                    class="col d-flex mt-2"
                    style="gap: 14px; justify-content: space-evenly;"
                  >
                    <base-checkbox
                      v-model="iMessage"
                      black
                      :checkdisabled="phone_number.length == 0"
                      :disabled="phone_number.length == 0"
                      block
                      label="iMessage"
                      style="margin: unset !important"
                    />
                    <base-checkbox
                      v-model="whatsapp"
                      black
                      :checkdisabled="phone_number.length == 0"
                      :disabled="phone_number.length == 0"
                      block
                      label="WhatsApp"
                      style="margin: unset !important"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="buttons-container d-flex justify-content-end">
              <base-button
                class="add-client-cancel-button"
                @click="onAdduser = false"
              >
                Cancel
              </base-button>
              <base-button
                class="add-client-button"
                :disabled="loading"
                @click="onSubmit"
              >
                <font-awesome-icon
                  v-if="loading"
                  icon="spinner"
                  class="mr-1"
                  spin
                />
                Add User
              </base-button>
            </div>
          </validation-observer>
        </form>
      </div>
    </base-modal>
    <!-- end modal -->
  </base-widget>
</template>
<script>

import { EventBus } from '@/event-bus';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import TeamCell from './Team/TeamCell';
import minHeightMixin from '../mixins/minheight';
import api from '../../_api';

export default {
  components: {
    TeamCell,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  mixins: [minHeightMixin],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: '',
    },
    isIntegrated: {
      type: Boolean,
      default: false,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
  },
  data: () => ({
    image: '',
    page: 1,
    onAdduser: false,
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role: 'stylist',
    iMessage: false,
    whatsapp: false,
    onFocusFisrtName: false,
    onFocusLastName: false,
    onFocusEmail: false,
    onFocusPhoneNumber: false,
    error_email: '',
    error_firstname: '',
    error_lastname: '',
    error_phonenumber: '',
    role_list: [{
      label: 'Stylist',
      value: 'stylist',
    }, {
      label: 'Admin',
      value: 'manager',
    }, {
      label: 'Admin/Stylist',
      value: 'manager-stylist',
    }],
  }),
  computed: {
    profilesCount() {
      return this.$store.state.profiles.count;
    },
  },
  methods: {
    filledInEverythingRequired() {
      // eslint-disable-next-line
      for (const field of this.getRequiredFields()) {
        if (!this.client[field]) {
          return false;
        }
      }
      return true;
    },
    isRequiredField(field) {
      this.error_email = '';
      this.error_firstname = '';
      this.error_lastname = '';
      this.error_phonenumber = '';
      const required = this.getRequiredFields();
      return required.indexOf(field) !== -1;
    },
    getRequiredFields() {
      return ['first_name', 'last_name', 'email'];
    },
    loadImage() {
      this.$refs.upload.click();
    },
    readFile(event) {
      this.uploadAndPatch(event.target.files[0]);
    },
    uploadAndPatch(file) {
      const reader = new FileReader();
      this.image = file;

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.image = reader.result;
      };
      reader.onerror = (error) => {
      };
    },
    trackSearch() {
      // Send track info to mixpanel
      this.$mixpanel.track('search', {
        location: '/admin/dashboard',
        account_id: this.$store.state.account.user.user.email,
      });
    },
    pageChange(newpage) {
      this.page = newpage;
      this.$emit('get-profiles-data', {
        page: this.page,
        page_size: this.pageSize,
        profile_sorting: true,
      });
    },
    onSearch(searchInput) {
      this.$emit('on-search', searchInput);
      this.page = 1;
    },
    onSubmit() {
      this.$refs.addUserForm.validate().then((result) => {
        if (result) {
          const user = JSON.parse(localStorage.getItem('user'));
          const userData = {
            first_name: this.first_name,
            last_name: this.last_name,
            email: this.email,
          };
          const payload = JSON.stringify({
            image: this.image,
            user: userData,
            role: this.role,
            imessage: this.iMessage,
            whatsapp: this.whatsapp,
            phone_number: this.phone_number,
          });
          api.post(`${this.api_root}/profile/`, payload, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: user ? `Token ${user.token}` : '',
            },
          })
            .then((response) => {
              this.onAdduser = false;
              EventBus.$emit('toast-success', `<b>${response.data.user.email}</b> was successfully added.`, 0, true);
            })
            .catch((error) => {
              const data_error = error.response.data;
              const data_key = Object.keys(data_error);
              data_key.forEach((element) => {
                if (element === 'email') {
                  // eslint-disable-next-line prefer-destructuring
                  this.error_email = data_error[element][0];
                } else if (element === 'phone_number') {
                  // eslint-disable-next-line prefer-destructuring
                  this.error_phonenumber = data_error[element][0];
                } else if (element === 'user') {
                  const user_error = data_error[element];
                  const user_key = Object.keys(user_error);

                  user_key.forEach((userrr) => {
                    if (userrr === 'first_name') {
                      // eslint-disable-next-line prefer-destructuring
                      this.error_firstname = user_error[userrr][0];
                    } else {
                      // eslint-disable-next-line prefer-destructuring
                      this.error_lastname = user_error[userrr][0];
                    }
                  });
                }
              });
            });
        }
      });
    },
  },
};
</script>
<style lang="scss">
.add-member {
  letter-spacing: 0.12px;
}
.search-bar {
  margin-left: auto;
  // min-width: 310px;

  &::v-deep input {
    // min-height: 42px;
    font-size: 0.875rem;
  }
}
</style>
<style lang="scss" scoped>
.avatar-placeholder {
  width: 200px;
  height: 200px;
  cursor: pointer;

  &__add-icon {
    position: absolute;
    bottom: 12px;
    right: 15px;
    cursor: pointer;
  }
}
.avatar {
  width: 200px;
  height: 200px;
  border-radius: 150px;
  object-fit: cover;
}
.avatar-file {
  display: none;
}
input[type="text"],
input[type="email"]{
  height: 44px;
  width: 100%;
  padding: 12px 16px !important;
  color: $dark-black !important;
  border: 1px solid #C6C6C6;
}
.was-validated .form-control:invalid, .was-validated .form-control--clean:invalid, .form-control.is-invalid, .is-invalid.form-control--clean{
  border-color: $validation-error-color;
}
::placeholder {
  color: #909090 !important;
  opacity: 1;
  font-size: 14px !important;
}
.divider {
  margin-top: 20px !important;
  margin-bottom: 40px !important;
  border-color: $gray-light-4;
}
.buttons-container {
  margin-top: 16px;
}
::v-deep .base-button--inverse {
  width: 168px;
  height: 40px;
  display: flex;
  justify-content: center;
  margin-inline: 0;
  background-color: $dark-black;
  font-family: $font-family-sans-serif-medium;
  font-weight: 600;
}

// Grids
.add-user-grid{
  padding-bottom: 32px;
  display: grid;
  grid-gap: 28px;
  grid-template-columns: [avatar] 193px [form] 1fr;
  align-items: center;

  &__form{
    display: grid;
    grid-row-gap: 12px;
  }
}
.form-grid{
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 12px;
  grid-row-gap: 16px;
}
::v-deep .base-button{
  margin: 0px 0px 0px 16px;
}
.add-client-cancel-button {
  height: 36px;
  border: 1px solid #C6C6C6;
  border-radius: 5px;
  padding-right: 11px !important;
  padding-left: 11px !important;
}
.add-client-button {
  height: 36px;
  background-color: $black;
  margin-left: 12px !important;
  padding-left: 11px !important;
  padding-right: 11px !important;
  color: white;

  &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }

  &:hover{
      background: darken($black, 5%) !important;
    }
}

@media (max-width: map-get($breakpoints,'lg')) {
  .avatar-placeholder {
    width: 200px;
    height: 200px;

    &__add-icon {
      bottom: 9px;
      right: 24px;
    }
  }
  .avatar {
    width: 200px;
    height: 200px;
  }
  ::v-deep .base-button--inverse {
    width: 160px;
  }
  .form-grid{
    grid-column-gap: 16px;
  }
  .buttons-container {
    margin-top: 48px;
  }
  .add-user-grid{
    grid-gap: 24px;
  }
}
.role{
  -webkit-appearance: none;
  appearance: none;
  color: #000000 !important;
  height: 44px;
  border-color: #C6C6C6 !important;
  background-image: url("~@/assets/icons/caret-down-grey.svg");
  background-size: 10px;
  background-repeat: no-repeat;
  background-position: calc(100% - 12px) center;
}
::v-deep .base-checkbox{
    margin-right: unset;
    &__icon{
      margin-right: 4px;
    }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active{
    -webkit-box-shadow: 0 0 0 30px white inset !important;
}

::v-deep .vs__dropdown-menu{
  color: #1B1B1B;
  font-family: 'SilkaLight';
  font-size: 14px;
  :hover{
    color: #1B1B1B;
    background-color: #F0F0F0;
  }
}
::v-deep .vs__dropdown-option--selected{
  :hover{
    color: #ABABAB;
    background-color: #F0F0F0;
  }
  font-family: 'SilkaLight';
  font-size: 14px;
}
::v-deep .vs__dropdown-option--highlight{
  background-color: #F0F0F0;
  color: #1B1B1B;
  font-family: 'SilkaLight';
  font-size: 14px;
  background-image: url("~@/assets/icons/check-bold-grey.svg");
  background-size: 10px;
  background-repeat: no-repeat;
  background-position: calc(100% - 12px) center;
}
::v-deep .v-select{
  -webkit-appearance: none;
  appearance: none;
  color: #000000 !important;
  height: 44px;
  border-color: #C6C6C6 !important;
  background-image: url("~@/assets/icons/chevron-down-grey.svg");

  background-size: 10px;
  background-repeat: no-repeat;
  background-position: calc(100% - 12px) center;
}
::v-deep .vs--open{
  -webkit-appearance: none;
  appearance: none;
  color: #000000 !important;
  height: 44px;
  border-color: #C6C6C6 !important;
  background-image: url("~@/assets/icons/chevron-up-grey.svg");
  background-size: 10px;
  background-repeat: no-repeat;
  background-position: calc(100% - 12px) center;

}
::v-deep .vs__open-indicator{
  display: none;
  // max-height: 5.75px;
  // height: 100%;
}

::v-deep .vs__search, ::v-deep .vs__search:focus{
  padding-left: 13px;
  padding-top: 5px;
}
.add-modal
  ::v-deep.modal-body{
    padding-top: unset !important;
    padding: 24px;
  }
::v-deep .base-heading{
  margin: unset !important;
}
::v-deep .close{
  position: relative;
  bottom: 7px;
  width: 13px;
  height: 13px;
}
::v-deep .modal-header{
  border-bottom: unset !important;
  padding: 26.5px 24px 0px 24px !important;
}
::v-deep .v-select .vs__selected-options .vs__selected {
  padding-top: 7px;
  padding-bottom: 5.5px;
}
</style>
